import { Tooltip } from '@material-ui/core';
import { SUPPLIER_GRADE, SUPPLIER_STATUS } from 'features/supplier/utils';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as DownSvg } from '@assets/icons/supplier/arrow-down.svg';
import { ReactComponent as UpSvg } from '@assets/icons/supplier/arrow-top.svg';

import NoData from '@components/NoData';

import InfoPopUp from '@features/supplier/component/InfoPopUp';

import { updateTextItemInGlobalFilter } from '@store/actions/FilterAction';

import { capitalizeFirstLetter, getNumberUnit, replaceUnderLine } from '@utils/Util';

import CommonImage from '@/components/CommonImage';
import CustomTooltip from '@/components/CustomTooltip';

const SupplierListTable = ({
	requestFrom,
	isLoading,
	onSupplierClick,
	allSuppliers,
	isShowMarginColumn,
	isShowShipCountColumn,
	isShowOnTimeColumn,
	isShowDevelopmentValue
}) => {
	const [selectedSorting, setSelectedSorting] = useState(null);
	const filterfields = useSelector((store) => store.filterFieldStore);
	const dispatch = useDispatch();

	const onSetSortingValue = () => {
		let activeValue = filterfields['sort'] && filterfields['sort'][0] && filterfields['sort'][0]['key'];
		setSelectedSorting(activeValue);
	};

	useEffect(() => {
		onSetSortingValue();
	}, [filterfields]);

	const handleChange = (value) => {
		let data = {
			name: 'sort',
			type: 'text',
			key: value,
			value: null,
			identifier: 'sort'
		};
		dispatch(updateTextItemInGlobalFilter(data));
	};

	const isActive = (value) => {
		if (value === selectedSorting) return true;
		else return false;
	};

	const renderExpireDocument = (documents = [], name) => {
		if (documents.length > 0) {
			return (
				<>
					<Tooltip
						title={documents.map((docName) => (
							<span className="brand-common-design mr-1 mb-1" key={`type_${docName}`}>
								{capitalizeFirstLetter(docName.replace('SUPPLIER_', '').replaceAll('_', ' '))}
							</span>
						))}
						placement="top"
						arrow
					>
						<span className="brand-common-design mb-1 status gray-1 radius">{name}</span>
					</Tooltip>
				</>
			);
		}
	};

	return (
		<div className="supplier-list-tabs-table">
			<table className="table with-border">
				<thead>
					<tr>
						<th>Supplier Name</th>
						<th>Supplier Type & Category</th>
						<th>
							<span className="w-100 d-flex align-items-center justify-content-between">
								<span>Status</span>
								<span className="d-flex flex-column justifiy-contnet-center arrow-icon">
									<UpSvg
										className={`${isActive('supplierStatus,asc') ? 'active' : ''}`}
										onClick={() => handleChange('supplierStatus,asc')}
									/>
									<DownSvg
										className={`${isActive('supplierStatus,desc') ? 'active' : ''}`}
										onClick={() => handleChange('supplierStatus,desc')}
									/>
								</span>
							</span>
						</th>
						<th>
							<span className="w-100 d-flex align-items-center justify-content-between">
								<span>Supplier Rank</span>
								<span className="d-flex flex-column justifiy-contnet-center arrow-icon">
									<UpSvg
										className={`${isActive('gradingMark,desc') ? 'active' : ''}`}
										onClick={() => handleChange('gradingMark,desc')}
									/>
									<DownSvg
										className={`${isActive('gradingMark,asc') ? 'active' : ''}`}
										onClick={() => handleChange('gradingMark,asc')}
									/>
								</span>
							</span>
						</th>
						{requestFrom !== 'CRM' && <th>Brand</th>}
						<th>Order Value</th>
						{isShowMarginColumn && <th>Order Margin</th>}
						{isShowShipCountColumn && <th>Shipped Styles</th>}
						{isShowOnTimeColumn && (
							<th>
								<div className="d-flex items-center justify-content-between gap-4">
									<p className="regular-14 gray_dark_02">On-time Shipment</p>

									<div>
										<CustomTooltip
											text="Based on shipment data from July 2025"
											arrowCenter={true}
										>
											<CommonImage src="/icons/info.svg" alt="info" />
										</CustomTooltip>
									</div>
								</div>{' '}
							</th>
						)}
						{isShowDevelopmentValue && <th className="development-support">Development Value</th>}
						<th>Compliance Grade</th>
						{requestFrom !== 'CRM' && <th>Price Range</th>}
						<th>About to Expire</th>
						<th>Expired</th>
					</tr>
				</thead>
				<tbody>
					{allSuppliers?.data?.map((item) => (
						<tr className="cursor-pointer" key={item.id} onClick={() => onSupplierClick(item)}>
							<td>
								<div className="">
									{!item?.isVisited && (
										<span className="status red-3 mr-1 mb-2">Audit Pending</span>
									)}
									<span className="d-block align-middle gray_dark_01 ml-1">
										<span className="weight-500">
											{item?.name}{' '}
											{requestFrom === 'CRM' && item.isRunning && (
												<Tooltip title={'Running'}>
													<img
														src={`${process.env.PUBLIC_URL}/icons/waves.svg`}
														alt="wave"
														className="ml-12"
													/>
												</Tooltip>
											)}
										</span>
										<br />
										<span className="regular-12 gray_dark_02">
											{item?.cityResponse}, {item?.countryResponse?.name}
										</span>
									</span>
								</div>
							</td>
							<td>
								<div className="d-flex flex-wrap">
									{item?.supplierTypeList?.map((type) => (
										<span className="status cyan-1 mr-1 mb-1" key={`type_${type}`}>
											{type}
										</span>
									))}

									{item?.supplierCategoryList?.map((category) => (
										<span className="status pink-1 mr-1 mb-1" key={`type_${category}`}>
											{replaceUnderLine(category, true)}
										</span>
									))}
								</div>
							</td>
							<td>
								<div className="grade-list ml-0">
									<span
										className={`status ${
											SUPPLIER_STATUS[item?.supplierStatus?.constant]
										} round`}
									>
										{item?.supplierStatus?.name}
									</span>
								</div>
							</td>
							<td>
								<div className="grade-list ml-0">
									<span
										className={`factory-grade ${
											SUPPLIER_GRADE[item?.supplierGrading?.constant]
										} font-weight-bold`}
									>
										{item?.supplierGrading?.name && (
											<InfoPopUp
												gradeId={item?.supplierGrading?.constant}
												iconType={'GRADE'}
												hoverTag={item?.supplierGrading?.name}
											/>
										)}
									</span>
								</div>
							</td>
							{requestFrom !== 'CRM' && (
								<td>
									<div className="d-flex flex-wrap">
										{item?.brandList?.slice(0, 3)?.map((brand) => (
											<span
												className="brand-common-design mr-1 mb-1"
												key={`type_${brand?.id}`}
											>
												{brand?.name}
											</span>
										))}

										{item?.brandList?.length > 3 && (
											<>
												<Tooltip
													title={item?.brandList?.slice(3)?.map((brand) => (
														<span
															className="brand-common-design mr-1 mb-1"
															key={`type_${brand?.id}`}
														>
															{brand?.name}
														</span>
													))}
													placement="top"
													arrow
												>
													<span className="brand-common-design mb-1 status gray-1 radius">
														+{parseInt(item?.brandList?.length) - 3}
													</span>
												</Tooltip>
											</>
										)}
									</div>
								</td>
							)}
							<td>{item?.orderValue && '$' + getNumberUnit(item?.orderValue)}</td>
							{isShowMarginColumn && (
								<td>
									{item?.marginAmount ? getNumberUnit(item?.marginAmount.toFixed(2)) : '--'}
									{item?.marginPercentage
										? ` (${item?.marginPercentage.toFixed(2) + '%'})`
										: '--'}
								</td>
							)}
							{isShowShipCountColumn && <td>{item?.styleShippedCount || '--'}</td>}
							{isShowOnTimeColumn && (
								<td>
									{item?.onTimePercentage ? item?.onTimePercentage.toFixed(2) + '%' : '--'}
								</td>
							)}

							{isShowDevelopmentValue && (
								<td>
									{item?.fabricTargetQty && (
										<p>
											Garments:
											{item?.fabricSampleDevelopmentQty || 0}/{item?.fabricTargetQty}{' '}
											Pcs
										</p>
									)}
									{item?.garmentsTargetQty && (
										<p>
											Fabric:
											{item?.garmentsSampleDevelopmentQty || 0}/
											{item?.garmentsTargetQty} Kg
										</p>
									)}
								</td>
							)}

							<td className="strenght-cell">
								{item?.certificateStrength?.constant && (
									<InfoPopUp
										gradeId={item?.certificateStrength?.constant}
										iconType={'CERTIFICATE_STRENGTH'}
										hoverTag={item?.certificateStrength?.name}
									/>
								)}
							</td>

							{requestFrom !== 'CRM' && (
								<td>
									{item?.pricePointList?.length > 0 && (
										<p>{item.pricePointList?.join(', ')}</p>
									)}
								</td>
							)}

							<td className="expired-cell">
								{renderExpireDocument(
									item?.auditReportAboutToExpireList,
									`${item?.auditReportAboutToExpireList?.length} Audit Report`
								)}
								{renderExpireDocument(
									item?.additionalDocumentAboutToExpireList,
									`${item?.additionalDocumentAboutToExpireList?.length} Additional Document`
								)}
								{renderExpireDocument(
									item?.certificateAboutToExpireList,
									`${item?.certificateAboutToExpireList?.length} Certificate`
								)}
								{renderExpireDocument(
									item?.regulatoryDocumentAboutToExpireList,
									`${item?.regulatoryDocumentAboutToExpireList?.length} Regulatory Document`
								)}
							</td>
							<td className="expired-cell">
								{renderExpireDocument(
									item?.auditReportExpiredList,
									`${item?.auditReportExpiredList?.length} Audit Report`
								)}
								{renderExpireDocument(
									item?.additionalDocumentExpiredList,
									`${item?.additionalDocumentExpiredList?.length} Additional Document`
								)}
								{renderExpireDocument(
									item?.certificateExpiredList,
									`${item?.certificateExpiredList?.length} Certificate`
								)}
								{renderExpireDocument(
									item?.regulatoryDocumentExpiredList,
									`${item?.regulatoryDocumentExpiredList?.length} Regulatory Document`
								)}
							</td>
						</tr>
					))}
				</tbody>
			</table>
			{allSuppliers?.data?.length === 0 && !isLoading && <NoData message={'No result found'} />}
		</div>
	);
};

export default SupplierListTable;
